import { useTranslation } from 'react-i18next'

import { Button } from '../../../components'
import { useCoverageJobStatus, CoverageJobProgressBody } from './CoverageJobProgressView'

/**
 * Inline job-progress view — same content as CoverageJobProgressModal but with
 * no Modal wrapper, so it renders *inside* the form's own modal (replacing the
 * fields) instead of stacking a second dialog on top. Mirrors the final screen
 * of the "create from Excel" wizard.
 *
 *   jobId       – the jid returned by the 202 response
 *   onClose     – called when the user dismisses a finished job. Receives the
 *                 finalStatus (terminal status, "submitted" on a poll error, or
 *                 "unavailable" when neutralOnError is set) AND the job's result
 *                 payload, so the caller can branch on what the job actually DID
 *                 (total_updated_rows / created_coverages_count) rather than only
 *                 on its status — a job can complete having changed nothing.
 *   onCompleted – called once the job reaches a non-failed terminal status; use
 *                 it to refetch the underlying list in the background.
 *   neutralOnError – treat a poll error as "status unavailable" instead of
 *                 "submitted" (no green check, no onCompleted). Set it wherever a
 *                 success claim over an unobserved outcome would mislead.
 *
 * While processing there is no button: the parent modal's close (✕) lets the
 * operator step away (the job keeps running server-side), just like the modal's
 * "Hide". On a terminal state a single "Close" button hands back finalStatus.
 */
export const CoverageJobProgressPanel = ({ jobId, onClose, onCompleted, statusFetcher, scope, neutralOnError: neutralOnErrorProp }) => {
  const { t } = useTranslation()

  const { data, isError, terminal, finalStatus, neutralOnError } = useCoverageJobStatus({
    jobId,
    enabled: true,
    onCompleted,
    statusFetcher,
    scope,
    neutralOnError: neutralOnErrorProp,
  })

  return (
    <div className="space-y-2">
      <CoverageJobProgressBody data={data} isError={isError} neutralOnError={neutralOnError} t={t} />

      {terminal && (
        <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
          <Button type="primary" className="px-6" onClick={() => onClose?.(finalStatus, data?.result)}>
            {t('coberturas.jobProgress.close')}
          </Button>
        </div>
      )}
    </div>
  )
}
